import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Flame, Star, Trophy, Medal, ShieldStar } from "@phosphor-icons/react";

const ALL_BADGES = [
  { name: "Rookie", xp: 100 },
  { name: "Apprentice", xp: 500 },
  { name: "Pro Learner", xp: 1000 },
  { name: "Career Builder", xp: 2500 },
  { name: "Elite", xp: 5000 },
];

export default function Profile() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/gamification/stats"); setStats(data); }
    catch (err) {
      console.error("gamification stats load failed", err);
    }
  })(); }, []);

  const xp = stats?.xp ?? user?.xp ?? 0;
  const level = stats?.level ?? user?.level ?? 1;
  const nextXp = stats?.next_level_xp ?? level * 100;
  const pct = Math.min(100, (xp / nextXp) * 100);
  const earned = new Set(stats?.badges || user?.badges || []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="profile-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-5xl mx-auto lg:mx-0">
        <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Player Profile</div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium break-words">{user?.name}</h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base break-words">{user?.email} · <span className="capitalize text-blue-400">{user?.tier || "free"}</span></p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 sm:mt-8">
          <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6" data-testid="profile-xp">
            <div className="flex items-center gap-2 text-blue-400 mb-2"><Star size={18} weight="fill" /><span className="text-sm">XP</span></div>
            <div className="text-4xl font-medium font-mono">{xp}</div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-zinc-500 mb-2"><span>Level {level}</span><span>Next: {nextXp} XP</span></div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} /></div>
            </div>
          </div>
          <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6" data-testid="profile-streak">
            <div className="flex items-center gap-2 text-orange-400 mb-2"><Flame size={18} weight="fill" /><span className="text-sm">Streak</span></div>
            <div className="text-4xl font-medium font-mono">{stats?.streak ?? user?.streak ?? 0}<span className="text-lg text-zinc-500"> days</span></div>
            <p className="text-xs text-zinc-500 mt-2">Keep showing up daily.</p>
          </div>
          <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6" data-testid="profile-level">
            <div className="flex items-center gap-2 text-amber-400 mb-2"><Trophy size={18} weight="fill" /><span className="text-sm">Level</span></div>
            <div className="text-4xl font-medium font-mono">{level}</div>
            <p className="text-xs text-zinc-500 mt-2">100 XP per level.</p>
          </div>
        </div>

        <h2 className="text-xl sm:text-2xl font-medium mt-10 sm:mt-12 mb-4">Badges</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="badges-grid">
          {ALL_BADGES.map((b) => {
            const got = earned.has(b.name);
            return (
              <div key={b.name} className={`rounded-xl p-5 text-center transition-all ${got ? "bg-amber-500/10 border border-amber-500/30 glow-blue" : "bg-[#0F0F12] border border-white/10 opacity-40"}`} data-testid={`badge-${b.name}`}>
                <ShieldStar size={36} weight={got ? "fill" : "duotone"} className={`mx-auto ${got ? "text-amber-400" : "text-zinc-600"}`} />
                <div className="text-sm font-medium mt-2">{b.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{b.xp} XP</div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
